import { useSearchParams } from 'react-router';
import { usePracticeSessions } from '@/hooks/use-seeds';
import { useDashboardStudents } from '@/hooks/use-dashboard';
import { SessionsTable } from '@/components/sessions/sessions-table';
import { SessionFilters } from '@/components/sessions/session-filters';
import { Skeleton } from '@/components/ui/skeleton';
import type { SessionSource } from '@/types/seeds';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

function dateInputToRFC3339Start(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00`).toISOString();
}

function dateInputToRFC3339End(value: string): string | undefined {
  if (!value) return undefined;
  // Exclusive upper bound: start of the next day.
  const d = new Date(`${value}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

export default function PracticeSessionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const perPage = Number(searchParams.get('per_page') ?? '20');
  const status = searchParams.get('status') ?? 'all';
  const source = searchParams.get('source') ?? 'all';
  const studentId = searchParams.get('student_id') ?? 'all';
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const sortOrder = searchParams.get('sort_order') ?? 'desc';

  const { data, isLoading } = usePracticeSessions({
    page,
    per_page: perPage,
    status: status === 'all' ? undefined : status,
    source: source === 'all' ? undefined : (source as SessionSource),
    student_id: studentId === 'all' ? undefined : studentId,
    date_from: dateInputToRFC3339Start(dateFrom),
    date_to: dateInputToRFC3339End(dateTo),
    sort_order: sortOrder,
  });

  const { data: studentsData } = useDashboardStudents({ per_page: 100 });

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === '' || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      <SessionFilters
        status={status}
        studentId={studentId}
        source={source}
        dateFrom={dateFrom}
        dateTo={dateTo}
        sortOrder={sortOrder}
        students={studentsData?.students}
        onStatusChange={(v) => v && updateParam('status', v)}
        onStudentChange={(v) => v && updateParam('student_id', v)}
        onSourceChange={(v) => v && updateParam('source', v)}
        onDateFromChange={(v) => updateParam('date_from', v)}
        onDateToChange={(v) => updateParam('date_to', v)}
        onSortOrderToggle={() => updateParam('sort_order', sortOrder === 'asc' ? 'desc' : 'asc')}
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <SessionsTable sessions={data?.sessions ?? []} />
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParam('page', String(Math.max(1, page - 1)))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => updateParam('page', String(p))}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updateParam('page', String(Math.min(totalPages, page + 1)))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
